import { Avatar } from "@mantine/core";
import { Link, useLocation, useSearchParams } from "react-router";
import styled from "styled-components";
import AreYouSureModal from "../common/Modals/AreYouSureModal";
import { useState, useEffect, useRef } from "react";
import { IconMenu2 } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import useIsMobile from "../../utils/useIsMobile";
import { media } from "../../theme";

interface NavbarProps {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
    companyLogo?: string;
}

const Navbar = ({ sidebarOpen, onToggleSidebar, companyLogo }: NavbarProps) => {
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout, company } = useAuth();

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab");
  const isMobile = useIsMobile();

  /**
   * URL parçası → kırıntı yolunda gösterilecek Türkçe ad.
   *
   * Burada karşılığı olmayan parça ham slug olarak basılıyordu
   * ("reports › monthly-report"). Adlar sidebar'daki başlıklarla birebir aynı
   * tutulmalı: kullanıcı menüde neye tıkladıysa üstte onu görmeli.
   */
  const pathMap: Record<string, string> = {
    dashboard: "Özet Rapor",
    calendar: "Takvim",
    employees: "Çalışanlar",
    tablets: "Tabletler",
    "panel-users": "Panel Kullanıcıları",
    "human-resources": "İnsan Kaynakları",
    "cv-pool": "CV Havuzu",
    // Raporlar
    reports: "Raporlar",
    "monthly-report": "Aylık Rapor",
    "entry-exit-reports": "Giriş/Çıkış Raporu",
    "correction-requests": "Çalışan Bildirimleri",
    // Vardiyalar
    shifts: "Vardiyalar",
    "work-shift": "Vardiya Listesi",
    "shift-assignment": "Personel Atama",
    "rotation-board": "Bu Hafta Kim Nerede",
    teams: "Ekipler",
    rotations: "Rotalar",
    // Personel detayı altındaki bölümler
    documents: "Belgeler",
    upload: "Yükle",
    "work-permits": "İzinler",
    // Ayarlar
    settings: "Ayarlar",
    general: "Genel Ayarlar",
    department: "Departman Ayarları",
    tags: "Rol ve Yetenek Ayarları",
    skill: "Yetenek Ayarları",
    role: "Rol Ayarları",
    contract: "Kontrat Ayarları",
    // Ortak eylemler
    new: "Yeni",
    create: "Yeni",
    edit: "Düzenle",
    update: "Düzenle",
    detail: "Detay",
  };

  /**
   * ?tab=... değerleri de kırıntı yolunun son halkası olarak basılıyor; burada
   * karşılığı olmayan sekme ham slug olarak görünür ("Departman Ayarları › teams").
   * Adlar sekme başlıklarıyla birebir aynı tutulmalı.
   */
  const tabLabelMap: Record<string, string> = {
    user: "Kullanıcılar",
    employee: "Çalışanlar",
    tablet: "Tabletler",
    teams: "Ekipler",
  };

  const segments = location.pathname.split("/").filter(Boolean);

  const breadcrumbs = segments
    .map((segment, index) => ({
      label: pathMap[segment] ?? segment,
      segment,
      fullPath: "/" + segments.slice(0, index + 1).join("/"),
      isId: !!segment.match(/^[a-f0-9]{24}$/),
    }))
    .filter((crumb) => !crumb.isId);

  // Detay rotaları (path bir ObjectId ile bitiyor, ör. /employees/:id) breadcrumb'da
  // yalnızca üst bölümü bırakır ve o da tıklanamaz olurdu. Bu durumda "Detay" crumb'ı
  // ekleyerek üst bölümü (ör. Çalışanlar) tıklanabilir bir geri dönüş linkine çeviririz.
  const lastSegment = segments[segments.length - 1];
  const isDetailRoute = !!lastSegment && /^[a-f0-9]{24}$/.test(lastSegment);

  const breadcrumbsWithTab = activeTab
    ? [
        ...breadcrumbs,
        {
          label: tabLabelMap[activeTab] ?? activeTab,
          segment: activeTab,
          fullPath: `${location.pathname}?tab=${activeTab}`,
          isId: false,
        },
      ]
    : isDetailRoute
    ? [
        ...breadcrumbs,
        {
          label: pathMap["detail"] ?? "Detay",
          segment: "detail",
          fullPath: location.pathname,
          isId: false,
        },
      ]
    : breadcrumbs;

  /**
   * Telefonda son iki halka gösterilir.
   *
   * BreadcrumbBar `overflow: hidden` olduğu için dar ekranda taşan kısım
   * SAĞDAN kırpılıyordu — yani kullanıcının bulunduğu yeri söyleyen son halka
   * kayboluyor, işe yaramayan ilk halkalar kalıyordu. Baştan kırpmak doğrusu:
   * bir üst seviye hâlâ tıklanabilir bir geri dönüş linki olarak duruyor.
   */
  const visibleCrumbs =
    isMobile && breadcrumbsWithTab.length > 2
      ? breadcrumbsWithTab.slice(-2)
      : breadcrumbsWithTab;

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const timeString = time.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <>
      <StyledNav>
        <LeftSide>
          <HamburgerButton onClick={onToggleSidebar}>
            <IconMenu2 size={28} color="#4c4c4c" />
          </HamburgerButton>

          {visibleCrumbs.length > 0 && (
            <BreadcrumbBar>
              {visibleCrumbs.map((crumb, i) => (
                <span key={crumb.fullPath + i}>
                  {i > 0 && <Separator>›</Separator>}
                  {i === visibleCrumbs.length - 1 ? (
                    <ActiveCrumb>{crumb.label}</ActiveCrumb>
                  ) : (
                    <CrumbLink to={crumb.fullPath}>{crumb.label}</CrumbLink>
                  )}
                </span>
              ))}
            </BreadcrumbBar>
          )}
        </LeftSide>

        <RightSide>
          <AvatarWrapper ref={dropdownRef}>
            <Avatar
              src={companyLogo}
              size={40}
              radius="xl"
              style={{ cursor: "pointer", border: "2px solid #d9d9d9" }}
              onClick={() => setDropdownOpen((p) => !p)}
            >
              {!companyLogo && company?.name?.[0]}
            </Avatar>

            {dropdownOpen && (
              <DropdownMenu>
                <CompanyName>{company?.name}</CompanyName>
                <Divider />
                <DropdownLogout
                  onClick={() => {
                    setDropdownOpen(false);
                    setOpenLogoutModal(true);
                  }}
                >
                  <Avatar src="/assets/logoutIcon.png" size={20} />
                  <span>Çıkış Yap</span>
                </DropdownLogout>
                <TimeText>{timeString}</TimeText>
              </DropdownMenu>
            )}
          </AvatarWrapper>
        </RightSide>
      </StyledNav>

      <AreYouSureModal
        opened={openLogoutModal}
        setOpened={setOpenLogoutModal}
        proccessFunc={() => logout()}
        title="Çıkış Yap"
        text="Çıkış yapmak istediğinizden emin misiniz?"
        confirmButtonText="Evet"
        cancelButtonText="Vazgeç"
      />
    </>
  );
};

export default Navbar;

const StyledNav = styled.nav`
  background-color: #ffffff;
  display: flex;
  flex-direction: row;
  padding: 0 10px;
  gap: 8px;
  border-bottom: 1px solid #d9d9d9;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  flex-shrink: 0;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  flex: 1;
  min-width: 0;

  ${media.mobile} {
    /* 30px boşluk dar ekranda kırıntı yolundan çalıyor. */
    gap: 12px;
  }
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const HamburgerButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  &:hover {
    background-color: #eef2ff;
  }
`;

const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  flex-wrap: nowrap;
  overflow: hidden;
  min-width: 0;

  ${media.mobile} {
    font-size: 14px;
  }
`;

const Separator = styled.span`
  color: #aaa;
  margin: 0 4px;
`;

const CrumbLink = styled(Link)`
  color: #5c26ff;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const ActiveCrumb = styled.span`
  color: #4c4c4c;
  font-weight: 600;
  white-space: nowrap;
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 52px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  min-width: 200px;
  padding: 12px 0;
  z-index: 100;
`;

const CompanyName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  padding: 4px 16px 10px;
`;

const Divider = styled.div`
  height: 1px;
  background: #d9d9d9;
  margin: 0 0 8px;
`;

const DropdownLogout = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background: #fff5f5;
  }
  span {
    font-size: 15px;
    font-weight: 600;
    color: red;
  }
`;

const TimeText = styled.div`
  text-align: center;
  font-size: 13px;
  color: #888;
  padding: 8px 16px 4px;
`;